'use client';

import { useCompleteTrackingContext } from '@hc-frontend/features-tracking';
import Script from 'next/script';
import { useEffect, useState } from 'react';

/**
 * Reddit advertising pixel ID for healthcare.com.
 */
const REDDIT_PIXEL_ID = 'a2_j15mu7naqdq3';

/**
 * Reddit base pixel. Injects the Reddit Pixel bootstrap snippet, initializes the
 * pixel with advanced matching, and fires a `PageVisit` event on every page.
 *
 * Advanced matching adds `externalId` set to the Snowplow `domainUserId` — a stable,
 * pseudonymous first-party identifier (the same id Criteo GUM uses) — to improve
 * Reddit's match rates without sending any PII. The id is read on the client, so the
 * snippet is built in an effect and rendered once resolved; if it is unavailable the
 * pixel still initializes (and fires `PageVisit`) without the `externalId` option.
 *
 * The inline snippet defines the synchronous `window.rdt` queue stub before the
 * async `pixel.js` loads. Loaded only in production from `global-scripts.tsx`.
 */
export function RedditPixel() {
  const { tracker } = useCompleteTrackingContext();
  const [snippet, setSnippet] = useState<string>('');

  useEffect(() => {
    const domainUserId = tracker.getDomainUserId();
    const initOptions = domainUserId ? `,{externalId:'${domainUserId}'}` : '';

    setSnippet(
      `!function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js?pixel_id=${REDDIT_PIXEL_ID}",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init','${REDDIT_PIXEL_ID}'${initOptions});rdt('track', 'PageVisit');`,
    );
  }, [tracker]);

  if (!snippet) {
    return null;
  }

  return (
    <Script
      id="reddit-pixel"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: snippet,
      }}
    />
  );
}
